// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { IButtonStyles, IStackStyles, IStyle, ITextStyles, ITheme } from '@fluentui/react';

export const cardContainerStyles = (theme: ITheme, isDisabled: boolean): IStackStyles => ({
  root: {
    backgroundColor: isDisabled ? theme.palette.neutralLighter : theme.palette.white,
    padding: '1rem',
    borderRadius: '0.25rem',
    boxShadow: theme.effects.elevation4
  }
});

export const titleTextStyles = (theme: ITheme, isDisabled: boolean): ITextStyles => ({
  root: {
    fontWeight: '600',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: isDisabled ? theme.semanticColors.disabledBodyText : theme.semanticColors.bodyText,
    wordBreak: 'break-word'
  }
});

export const durationContainerStyles: IStackStyles = {
  root: {
    // On mobile and narrow widths do not show the duration field per design
    '@media only screen and (max-width: 600px)': {
      display: 'none'
    }
  }
};

const detailsMarginREM = 4;
const joinButtonWidthREM = 6;

export const detailsContainerStyles: IStackStyles = {
  root: {
    width: '16.75rem',

    '@media only screen and (max-width: 600px)': {
      width: `calc(100% - ${joinButtonWidthREM + detailsMarginREM}rem)`
    }
  }
};

export const callSubDetailsTextStyles = (theme: ITheme, isDisabled: boolean): ITextStyles => ({
  root: {
    fontSize: '0.75rem',
    color: isDisabled ? theme.semanticColors.disabledBodySubtext : theme.palette.neutralSecondary,
    lineHeight: '1.1rem',
    width: '95%',

    // Support ellipsis for long text
    display: 'block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',

    // For browsers that support it, have two lines of text before ellipsis kicks in
    '@supports (-webkit-line-clamp: 2)': {
      display: '-webkit-box',
      whiteSpace: 'initial',
      '-webkit-line-clamp': '2',
      '-webkit-box-orient': 'vertical'
    }
  }
});

const joinButtonTextContainerStyles: IStyle = {
  fontWeight: '600',
  fontSize: '0.875rem',
  whiteSpace: 'nowrap'
};

export const joinCallActiveButtonStyles: IButtonStyles = {
  root: {
    padding: '0 0.25rem'
  },
  textContainer: joinButtonTextContainerStyles,
  icon: {
    height: '0.875rem'
  }
};

export const joinCallButtonStyles = (theme: ITheme, showAsDisabled: boolean): IButtonStyles => ({
  root: {
    padding: '0 0.25rem',
    borderColor: theme.palette.neutralQuaternaryAlt,
    backgroundColor: showAsDisabled ? theme.semanticColors.buttonBackgroundDisabled : undefined,
    color: showAsDisabled ? theme.semanticColors.buttonTextDisabled : undefined
  },
  textContainer: joinButtonTextContainerStyles,
  splitButtonMenuButton: {
    borderColor: theme.palette.neutralQuaternaryAlt,
    backgroundColor: showAsDisabled ? theme.semanticColors.buttonBackgroundDisabled : undefined
  },
  splitButtonMenuIcon: {
    color: showAsDisabled ? theme.semanticColors.buttonTextDisabled : undefined
  }
});
